import { Link } from 'react-router-dom';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

const steps = [
  {
    number: '01',
    title: 'Free Inspection & Assessment',
    desc: 'We conduct a complete on-site inspection of your roof, flashing, gutters, masonry, and related structures. We identify all issues — not just the obvious ones.',
    icon: 'ri-search-eye-line',
  },
  {
    number: '02',
    title: 'Written Estimate & Timeline',
    desc: 'You receive a detailed written estimate outlining scope of work, materials, pricing, and project timeline. No vague verbal quotes. No hidden costs.',
    icon: 'ri-file-text-line',
  },
  {
    number: '03',
    title: 'Material Selection',
    desc: "We recommend the right materials for your roof type, budget, and Chicago's specific climate demands — from TPO and EPDM to architectural shingles and metal.",
    icon: 'ri-palette-line',
  },
  {
    number: '04',
    title: 'Expert Installation',
    desc: "Our experienced crew handles installation with precision. We're on-site, on time, and manage every detail from setup through cleanup.",
    icon: 'ri-hammer-line',
  },
  {
    number: '05',
    title: 'Walkthrough & Quality Check',
    desc: "Before we leave, we walk the completed project with you, address any questions, and confirm everything meets our strict quality standards.",
    icon: 'ri-checkbox-circle-line',
  },
];

export default function ProcessSection() {
  return (
    <section className="bg-brand-dark py-24 relative overflow-hidden">
      {/* Diagonal top cut for depth */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-white" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 100%)' }} />

      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'repeating-linear-gradient(45deg, #c9a227 0, #c9a227 1px, transparent 0, transparent 20px)', backgroundSize: '28px 28px' }}
      />

      {/* Radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-green/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-4">
        {/* Header */}
        <RevealOnScroll className="text-center mb-16">
          <span className="text-brand-gold text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
            <span className="w-8 h-px bg-brand-gold inline-block" />
            How It Works
            <span className="w-8 h-px bg-brand-gold inline-block" />
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mt-4 mb-4">
            A Proven Process Built for<br />
            <span className="text-brand-gold italic">High-Quality Results</span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto leading-relaxed">
            Every Green World project follows the same five-phase approach — designed to protect your
            investment and eliminate surprises from start to finish.
          </p>
        </RevealOnScroll>

        {/* Steps - horizontal connected flow */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
          {steps.map((step, idx) => (
            <RevealOnScroll key={step.number} delay={idx * 100}>
              <div className="relative flex flex-col h-full">
                {/* Connecting line */}
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[calc(100%+8px)] w-full h-0.5 bg-gradient-to-r from-brand-gold/40 to-transparent z-0" />
                )}

                <div className="relative z-10 group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-brand-gold/40 rounded-xl p-6 transition-all duration-300 h-full flex flex-col cursor-default">
                  {/* Step number + icon */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="relative w-14 h-14 flex-shrink-0">
                      <div className="absolute inset-0 bg-brand-gold/15 rounded-xl group-hover:bg-brand-gold/25 transition-colors" />
                      <div className="relative w-full h-full flex items-center justify-center">
                        <i className={`${step.icon} text-brand-gold text-2xl`} />
                      </div>
                    </div>
                    <span className="font-display text-4xl font-bold text-white/15 group-hover:text-brand-gold/30 transition-colors leading-none">
                      {step.number}
                    </span>
                  </div>

                  <h3 className="text-white font-bold text-sm mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed flex-1">{step.desc}</p>
                </div>
              </div>
            </RevealOnScroll>
          ))}
        </div>

        {/* CTA */}
        <RevealOnScroll className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line" />
            Start With a Free Inspection
            <i className="ri-arrow-right-line" />
          </Link>
        </RevealOnScroll>
      </div>
    </section>
  );
}
